<script setup>
import { Switch, SwitchGroup, SwitchDescription, SwitchLabel } from '@headlessui/vue';
import { computed, useSlots } from 'vue';
import { useFormField } from '@/shared/composables/useFormField.js';
import toggleTheme from '@/ui/Toggle/index.js';

const props = defineProps({
    id: {
        type: String,
        default: undefined,
    },
    name: {
        type: String,
        default: undefined,
    },
    label: {
        type: String,
        default: undefined,
    },
    description: {
        type: String,
        default: undefined,
    },
    size: {
        type: String,
        default: 'md',
        validator: (v) => ['sm', 'md', 'lg'].includes(v),
    },
    disabled: {
        type: Boolean,
        default: false,
    },
    loading: {
        type: Boolean,
        default: false,
    },
    color: {
        type: String,
        default: 'accent',
        validator: (v) => ['primary', 'accent'].includes(v),
    },
});

const enabled = defineModel({
    type: Boolean,
    required: true,
});

const slots = useSlots();
const { id: inputId, name: inputName } = useFormField(props);

const ui = computed(() =>
    toggleTheme({
        color: props.color,
        size: props.size,
        disabled: props.disabled,
    })
);
</script>

<template>
    <SwitchGroup>
        <div :class="ui.root()">
            <div :class="ui.container()">
                <Switch
                    :id="inputId"
                    v-model="enabled"
                    :name="inputName"
                    :disabled="disabled || loading"
                    :data-state="enabled ? 'checked' : 'unchecked'"
                    :class="ui.base()"
                    @keyup.prevent
                    @keydown.enter.prevent="enabled = !enabled"
                    @keydown.space.prevent="enabled = !enabled"
                >
                    <span
                        aria-hidden="true"
                        :data-state="enabled ? 'checked' : 'unchecked'"
                        :class="ui.thumb()"
                    />
                </Switch>
            </div>
            <div
                v-if="label || !!slots.label || description || !!slots.description"
                :class="ui.wrapper()"
            >
                <SwitchLabel
                    v-if="label || !!slots.label"
                    :for="inputId"
                    :class="ui.label()"
                >
                    <slot
                        name="label"
                        :label="label"
                    >
                        {{ label }}
                    </slot>
                </SwitchLabel>
                <SwitchDescription
                    v-if="description || !!slots.description"
                    :class="ui.description()"
                >
                    <slot
                        name="description"
                        :description="description"
                    >
                        {{ description }}
                    </slot>
                </SwitchDescription>
            </div>
        </div>
    </SwitchGroup>
</template>
